import React, { useState, useCallback, useMemo } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableFooter from '@mui/material/TableFooter';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Slider from '@mui/material/Slider';
import cn from 'classnames';
import { DataGeneratorSimpleRelationship } from 'libs/end-to-end/src/data-generator/data-generator-input';
import { DataGeneratorSimpleRelationshipValue } from 'libs/end-to-end/src/data-generator/data-generator-input';
import { getColorMatrix } from 'libs/end-to-end/src/data-generator/data-generator-heatmap-updater';

const correlationColors: Record<DataGeneratorSimpleRelationshipValue, string> =
  {
    strongNegative: 'bg-red-500',
    moderateNegative: 'bg-red-400',
    weakNegative: 'bg-red-200',
    neutral: 'bg-blue-50',
    weakPositive: 'bg-blue-200',
    moderatePositive: 'bg-blue-400',
    strongPositive: 'bg-blue-500',
  };

const hoverCorrelationColors: Record<
  DataGeneratorSimpleRelationshipValue,
  string
> = {
  strongNegative: 'bg-red-600',
  moderateNegative: 'bg-red-500',
  weakNegative: 'bg-red-300',
  neutral: 'bg-blue-100',
  weakPositive: 'bg-blue-300',
  moderatePositive: 'bg-blue-500',
  strongPositive: 'bg-blue-600',
};

export type CorrelationHeatmapRowValue =
  DataGeneratorSimpleRelationshipValue | null;

export interface CorrelationHeatmapDataRow {
  name: string;
  record: Record<string, CorrelationHeatmapRowValue>;
}

const correlationMarks = [
  {
    value: -1,
    label: 'Strongly Negative',
    acronym: 'SN',
  },
  {
    value: -0.5,
    label: 'Moderately Negative',
    acronym: 'MN',
  },
  {
    value: -0.25,
    label: 'Weakly Negative',
    acronym: 'WO',
  },
  {
    value: 0,
    label: 'Neutral',
    acronym: 'N',
  },
  {
    value: 0.25,
    label: 'Weakly Positive',
    acronym: 'WP',
  },
  {
    value: 0.5,
    label: 'Moderately Positive',
    acronym: 'MP',
  },
  {
    value: 1,
    label: 'Positive',
    acronym: 'P',
  },
];

const correlationNumberToString: Record<
  string,
  DataGeneratorSimpleRelationshipValue
> = {
  '-1': 'strongNegative',
  '-0.5': 'moderateNegative',
  '-0.25': 'weakNegative',
  '0': 'neutral',
  '0.25': 'weakPositive',
  '0.5': 'moderatePositive',
  '1': 'strongPositive',
};

const correlationStringToNumber: Record<
  DataGeneratorSimpleRelationshipValue,
  number
> = {
  strongNegative: -1,
  moderateNegative: -0.5,
  weakNegative: -0.25,
  neutral: 0,
  weakPositive: 0.25,
  moderatePositive: 0.5,
  strongPositive: 1,
};

const getAcronym = (value: CorrelationHeatmapRowValue) => {
  if (!value) {
    return value;
  }

  return (
    correlationMarks.find(
      (mark) => mark.value === correlationStringToNumber[value],
    )?.acronym ?? value
  );
};

interface CorrelationHeatmapProps {
  relationships: DataGeneratorSimpleRelationship[];
  handleOnChange: (values: DataGeneratorSimpleRelationship[]) => void;
  uniqueColumns: string[];
}

const computedBackgroundColor = (cellColor: number) => {
  if (cellColor < 0) {
    // Red scale: -1 (darkest) to 0 (lightest)
    const intensity = Math.abs(cellColor);
    const lightness = 100 - intensity * 60; // 40% to 100%
    return `hsl(0, 100%, ${lightness}%)`;
  } else if (cellColor > 0) {
    // Blue scale: 0 (lightest) to 1 (darkest)
    const intensity = cellColor;
    const lightness = 100 - intensity * 60; // 40% to 100%
    return `hsl(210, 100%, ${lightness}%)`;
  } else {
    // Neutral (0)
    return 'hsl(210, 50%, 95%)';
  }
};

export function CorrelationHeatmap({
  relationships,
  handleOnChange,
  uniqueColumns,
}: CorrelationHeatmapProps) {
  const columns = useMemo(() => {
    return uniqueColumns.map((column) => ({
      id: column,
      label: column,
    }));
  }, [uniqueColumns]);

  const rows: CorrelationHeatmapDataRow[] = useMemo(() => {
    return convertPairsToRows({
      pairs: relationships,
      columnNames: uniqueColumns,
    });
  }, [uniqueColumns, relationships]);

  const [selectedCell, setSelectedCell] = useState<{
    columnA: string;
    columnB: string;
  } | null>(null);

  const selectedCellCurrentValue: DataGeneratorSimpleRelationshipValue | null =
    useMemo(() => {
      if (!selectedCell) return null;

      const pair = relationships.find(
        (pair) =>
          pair.columnA === selectedCell.columnA &&
          pair.columnB === selectedCell.columnB,
      );
      if (!pair) return 'neutral';

      return pair.value;
    }, [selectedCell, relationships]);

  const handleCellClick = useCallback(
    (rowIndex: number, colIndex: number) => {
      const columnAId = columns[rowIndex]?.id ?? null;
      const columnBId = columns[colIndex]?.id ?? null;

      if (!columnAId || !columnBId) {
        return;
      }

      setSelectedCell({
        columnA: columnAId,
        columnB: columnBId,
      });
    },
    [columns],
  );

  const handleSliderChange = useCallback(
    (event: Event, newValue: number | number[]) => {
      if (!selectedCell) return;

      const correlationStringValue =
        correlationNumberToString[String(newValue)] ?? 'neutral';

      const newPairs = [
        ...relationships.filter(
          (pair) =>
            pair.columnA !== selectedCell.columnA ||
            pair.columnB !== selectedCell.columnB,
        ),
        {
          columnA: selectedCell.columnA,
          columnB: selectedCell.columnB,
          value: correlationStringValue,
        },
      ];

      handleOnChange(newPairs);
    },
    [selectedCell, relationships, handleOnChange],
  );

  const colorMatrix = useMemo(() => {
    return getColorMatrix(
      columns.map((col) => col.label),
      relationships,
    );
  }, [columns, relationships]);

  return (
    <div className="w-full flex gap-4">
      <div className="w-fit">
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 500 }} aria-label="custom pagination table">
            <TableBody>
              {rows.map((row, rowIndex) => (
                <TableRow key={row.name}>
                  <TableCell component="th" scope="row">
                    {row.name}
                  </TableCell>
                  {columns
                    .filter((col, colIndex) => colIndex < rowIndex)
                    .map((col, colIndex) => {
                      const colValue: CorrelationHeatmapRowValue =
                        row.record[col.id];

                      const backgroundColorPalette =
                        col.id === selectedCell?.columnB &&
                        row.name === selectedCell?.columnA
                          ? hoverCorrelationColors
                          : correlationColors;

                      const backgroundColor =
                        colValue !== null
                          ? backgroundColorPalette[colValue]
                          : undefined;

                      const hoverBackgroundColor =
                        colValue !== null
                          ? hoverCorrelationColors[colValue]
                          : undefined;

                      const cellColor = colorMatrix.matrix[rowIndex][colIndex];

                      // Map cellColor (-1 to 1) to a CSS color
                      const cellColorHSL = computedBackgroundColor(cellColor);

                      return (
                        <TableCell
                          key={col.id}
                          style={{
                            width: 160,
                            backgroundColor: cellColorHSL,
                          }}
                          className={cn([
                            backgroundColor,
                            `hover:${hoverBackgroundColor}`,
                            'cursor-pointer',
                          ])}
                          onClick={() => handleCellClick(rowIndex, colIndex)}
                        >
                          {getAcronym(colValue)}
                        </TableCell>
                      );
                    })}
                </TableRow>
              ))}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TableCell></TableCell>
                {columns.map((col) => (
                  <TableCell variant="body" key={col.id} align="center">
                    {col.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      </div>
      <div>
        {selectedCell !== null && selectedCellCurrentValue !== null && (
          <Slider
            aria-label="Correlation"
            orientation="vertical"
            marks={correlationMarks}
            valueLabelDisplay="auto"
            value={correlationStringToNumber[selectedCellCurrentValue]}
            step={null}
            min={-1}
            max={1}
            onChange={handleSliderChange}
          />
        )}
      </div>
    </div>
  );
}

const convertPairsToRows = ({
  pairs,
  columnNames,
}: {
  pairs: DataGeneratorSimpleRelationship[];
  columnNames: string[];
}): CorrelationHeatmapDataRow[] => {
  const rows: CorrelationHeatmapDataRow[] = [];

  for (let rowIndex = 0; rowIndex < columnNames.length; rowIndex++) {
    const rowName = columnNames[rowIndex];
    const rowRecord: Record<string, CorrelationHeatmapRowValue> = {};

    for (let colIndex = 0; colIndex < columnNames.length; colIndex++) {
      if (colIndex >= rowIndex) {
        continue;
      }

      const columnName = columnNames[colIndex];

      const pair = pairs.find(
        (p) =>
          (p.columnA === rowName && p.columnB === columnName) ||
          (p.columnA === columnName && p.columnB === rowName),
      );

      rowRecord[columnName] = pair ? pair.value : 'neutral';
    }

    rows.push({
      name: rowName,
      record: rowRecord,
    });
  }

  return rows;
};
